import React, { useContext, useEffect, useMemo, useState } from 'react';
import { fetchMenuAction, resetMenu } from '@kfc-global/react-shared/redux/Actions/MenuAction';
import { UserOrderContext } from 'context/context';
import { useDispatch, useSelector } from 'react-redux';
import { getDispositionTypeOnAPIFormat, isEmptyEvery, createBasketData } from 'common/utilities/utils';
import { HOME_PAGE_CONSTANT_DATA } from 'common/utilities/homePageModulator';
import { isEmpty } from 'lodash';
import START_ORDER_CONSTANTS from 'organisms/StartOrderComponent/Constants/startOrderConstants';
import { getDefaultDispositionData } from 'common/utilities/CateringPLPUtils';
import { getIsMenuNavigated, saveScheduleDate } from 'organisms/MenusPLP/AU/MenusPlpUtilities/MenusPlpUtils';
import { getStoreStatusForTimeSaga } from '@kfc-global/react-shared/redux/Actions/StoresAction';
import { createBasketsAction } from '@kfc-global/react-shared/redux/Actions/CartAction';
import { LANGUAGE_CHANGED, LOCAL_STORAGE_KEYS, DATE_FORMATS } from 'common/constants/SharedConstants';
import { getPWAstorage, resetPWAstorage } from '@kfc-global/react-shared/redux/Slices/PwaStorageSlice';
import { useAppSelector, useAppDispatch } from '@kfc-global/react-shared/redux/Store/hooks';
import { getEpochTime } from 'common/utilities/dayJsUtils';

const catalogName = (storesWithStatus, defaultDispositionData, serviceType, storeInfo) => {
  const storesWithStatusServiceCheck = storesWithStatus?.filter(store => store?.service === serviceType);
  return (
    storesWithStatusServiceCheck?.[0]?.summary?.menu?.catalogs?.[0] ||
    storeInfo?.summary?.menu?.catalogs?.[0] ||
    defaultDispositionData.defaultMenuName
  );
};

function WithMenuData(Component) {
  const { SET_FORCERELOAD, STATUS_API_CALLED } = START_ORDER_CONSTANTS;
  function isShouldFetchMenuData(flatResponse, storeId, serviceType, forceReload, userOrderStateDispatch) {
    let shouldFetchMenuData = isEmpty(flatResponse) || forceReload;
    if (forceReload) {
      userOrderStateDispatch({
        type: SET_FORCERELOAD,
        value: { forceReload: false },
      });
    }

    if (!shouldFetchMenuData && storeId) {
      const cachedStoreId = localStorage.getItem('storeId');
      if (!cachedStoreId || storeId !== cachedStoreId) {
        localStorage.setItem('storeId', storeId);
        shouldFetchMenuData = true;
      }
    }

    if (!shouldFetchMenuData && serviceType) {
      const cachedServiceType = localStorage.getItem('serviceType');
      if (!cachedServiceType || serviceType !== cachedServiceType) {
        localStorage.setItem('serviceType', serviceType);
        shouldFetchMenuData = true;
      }
    }

    return shouldFetchMenuData;
  }

  const getDispositionTypeHOC = (dispositionType, defaultdispostitionType) => {
    return dispositionType ?? defaultdispostitionType;
  };

  return function WithMenuDataComponent({ ...props }) {
    const {
      flatResponse = {},
      searchResults: { id: categoriesId = null, categories: baseCategory = [] } = {},
      isMenuReset,
      isLoading: isLoadingFetchMenu,
    } = useSelector(({ menuReducer }) => menuReducer) ?? {};
    const {
      userOrderState: { dispositionNew = JSON.parse(localStorage.getItem('dispositionNew')), forceReload } = {},
      userOrderStateDispatch,
    } = useContext(UserOrderContext);
    const [isRedirectedUser, setIsRedirectedUser] = useState(false);
    const disposition = isEmptyEvery(dispositionNew)
      ? JSON.parse(localStorage.getItem('dispositionNew'))
      : dispositionNew;
    const cartReducer = useSelector(data => data?.cartReducer);
    const { storesWithStatus } = useSelector(data => data?.storesReducer) || {};
    const getPwaStorageData = useAppSelector(getPWAstorage);
    const sliceDispatch = useAppDispatch();
    const showOrderNow = cartReducer?.cartData?.requestedDateTime?.isForNow;
    const {
      tenantId,
      authoredContent: { defaultdisposition, defaultcateringdisposition } = {},
      genericMenuName,
      genericCateringName,
      componentOptions,
    } = useSelector(data => data.tenantReducer || {});
    const dispositionConfig = componentOptions?.web?.dispositionConfig;
    const { enableScheduleOrder = false, enableClickCollectScheduleOrder = false } = dispositionConfig;
    const allowScheduleOrderForDispositions =
      componentOptions?.web?.scheduledDelivery?.allowScheduleOrderForDispositions ?? [];
    const enableScheduleOrderFlag = enableScheduleOrder || enableClickCollectScheduleOrder;
    const defaultDispositionData = getDefaultDispositionData({
      path: props?.location?.pathname,
      genericMenuName,
      genericCateringName,
      defaultdisposition,
      defaultcateringdisposition,
    });
    const futureDateTime = cartReducer?.cartData?.requestedDateTime?.futureDateTime ?? '';
    const { SERVICE_CHANNEL } = HOME_PAGE_CONSTANT_DATA;
    const params = useMemo(() => {
      const dispositionTypeHOC = getDispositionTypeHOC(
        disposition?.type,
        defaultDispositionData.defaultdispostitionType,
      );
      return {
        storeInfo: disposition?.store ?? {},
        dispositionType: getDispositionTypeOnAPIFormat(dispositionTypeHOC),
        time:
          disposition?.date && disposition?.time
            ? getEpochTime(`${disposition?.date} ${disposition?.time}`, DATE_FORMATS.YYYY_MM_DD_h_mm_A)
            : futureDateTime,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disposition, defaultdisposition, props?.location]);
    /* istanbul ignore next */
    /**
     * Call to get setUserParams
     */
    const { storeInfo, dispositionType, time } = params;
    const serviceType = dispositionType;
    const catalogs = catalogName(storesWithStatus, defaultDispositionData, serviceType, storeInfo);
    const storeId = storeInfo?.id || storeInfo?.storeId;
    let fetchMenuPayload = { storeId: storeId, channel: SERVICE_CHANNEL, service: serviceType };
    const scheduleOrderObject = { enableScheduleOrderFlag, showOrderNow, allowScheduleOrderForDispositions };
    const updatedFetchMenuPayload = saveScheduleDate(fetchMenuPayload, serviceType, scheduleOrderObject);
    const dispatch = useDispatch();

    const createBasketActionHandler = async () => {
      const obj = await createBasketData(storesWithStatus[0]);
      dispatch(createBasketsAction({ data: obj, tenantId: tenantId }));
      const dispositionInfo = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DISPOSITION_NEW));
      dispositionInfo.store.summary = storesWithStatus?.[0]?.summary;
      localStorage.setItem(LOCAL_STORAGE_KEYS.DISPOSITION_NEW, JSON.stringify(dispositionInfo));
    };
    const triggerStatusAndCatalogAPI = () => {
      const userTimeSlot = !isNaN(time) ? time : '';
      const payload = {
        storeid: storeId,
        service: serviceType,
        channel: SERVICE_CHANNEL,
      };
      const finalPayload = userTimeSlot ? { ...payload, futureDateTimeInEpochFormat: userTimeSlot } : payload;
      dispatch(
        getStoreStatusForTimeSaga(
          { data: finalPayload, tenantId: tenantId },
          () => {
            userOrderStateDispatch({ type: STATUS_API_CALLED, value: { statusAPICalled: true } });
            dispatch(
              fetchMenuAction(
                {
                  tenantId: tenantId,
                  catalogName: catalogs,
                },
                updatedFetchMenuPayload,
                () => {
                  if (getPwaStorageData?.LanguageChanged && cartReducer?.cartData?.id) {
                    createBasketActionHandler();
                    setTimeout(() => sliceDispatch(resetPWAstorage({ key: LANGUAGE_CHANGED })), 3000);
                  }
                },
              ),
            );
          },
          null,
        ),
      );
    };

    useEffect(() => {
      const isMenuNavigated = !storeId && getIsMenuNavigated(props?.location?.pathname, baseCategory);
      const shouldFetchMenuData = isShouldFetchMenuData(
        flatResponse,
        storeId,
        serviceType,
        forceReload,
        userOrderStateDispatch,
      );
      if (isMenuNavigated) {
        dispatch(resetMenu());
      }
      if (getFetchMenuAction(isMenuNavigated, shouldFetchMenuData)) {
        setIsRedirectedUser(false);
        if (storeId && serviceType) {
          triggerStatusAndCatalogAPI();
        } else {
          dispatch(
            fetchMenuAction(
              {
                tenantId: tenantId,
                catalogName: catalogs,
              },
              updatedFetchMenuPayload,
            ),
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      SERVICE_CHANNEL,
      catalogs,
      dispatch,
      flatResponse,
      serviceType,
      storeId,
      tenantId,
      forceReload,
      isRedirectedUser,
      time,
    ]);

    const getFetchMenuAction = (isMenuNavigated, shouldFetchMenuData) => {
      return (
        (!isLoadingFetchMenu || (categoriesId && categoriesId !== catalogs) || storeId) &&
        (isMenuNavigated || ((shouldFetchMenuData || isRedirectedUser) && !isMenuReset))
      );
    };

    useEffect(() => {
      setIsRedirectedUser(true);
      userOrderStateDispatch({ type: STATUS_API_CALLED, value: { statusAPICalled: false } });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.location?.pathname]);
    return !isEmpty(flatResponse) ? <Component {...props} /> : <></>;
  };
}
export default WithMenuData;
